/** API client — no custom headers (avoids CORS preflight blocks). */

const BUILD_API = process.env.REACT_APP_IP_API_URL || '';
const PRODUCTION_API = 'https://tyszt4mfki.execute-api.ap-southeast-2.amazonaws.com/ip/v1';
const FETCH_TIMEOUT_MS = 45000;
const MAX_RETRIES = 3;

let resolvedApiBase = null;
let resolvePromise = null;

function sanitizeApiError(message, tokenKey) {
  const msg = message || 'Request failed';
  if (/session expired|sign in again|unauthorized|401/i.test(msg)) {
    if (tokenKey === 'hcen_platform_token') {
      return 'Connection issue — use Retry below. You stay signed in; no need to log in again unless Retry fails.';
    }
    return 'Could not load data — tap Retry or refresh the page. You stay signed in.';
  }
  if (/hospital not found/i.test(msg)) {
    return 'Hospital data is loading — tap Retry. If this hospital was just onboarded, wait a moment and refresh.';
  }
  if (/failed to fetch|network|timeout/i.test(msg)) {
    return 'Network slow — tap Retry. Your login is still active.';
  }
  return msg;
}

function staticApiBase() {
  if (BUILD_API) return BUILD_API.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (
      host === 'networks.hcen.in' ||
      host === 'www.networks.hcen.in' ||
      host.endsWith('.cloudfront.net')
    ) {
      return PRODUCTION_API;
    }
  }
  return '';
}

function apiCandidates() {
  const list = [];
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (
      host === 'networks.hcen.in' ||
      host === 'www.networks.hcen.in' ||
      host.endsWith('.cloudfront.net')
    ) {
      list.push(`${window.location.origin}/ip/v1`);
    }
  }
  const built = staticApiBase();
  if (built && !list.includes(built)) list.push(built);
  return list;
}

async function probeHealth(base) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const res = await fetch(`${base}/health`, { signal: controller.signal });
    clearTimeout(timer);
    if (res.status === 500) return false;
    if (!res.ok) return false;
    const text = await res.text();
    try {
      const data = JSON.parse(text);
      return !!data?.ok;
    } catch {
      return false;
    }
  } catch {
    clearTimeout(timer);
    return false;
  }
}

/** Pick fastest reachable API base (same-origin CloudFront route preferred). */
export async function ensureApiReady() {
  if (resolvedApiBase) return resolvedApiBase;
  if (resolvePromise) return resolvePromise;
  resolvePromise = (async () => {
    const candidates = apiCandidates();
    for (const base of candidates) {
      if (await probeHealth(base)) {
        resolvedApiBase = base;
        return base;
      }
    }
    // Prefer known gateway URL even when health fails so login can surface the real error.
    resolvedApiBase = staticApiBase() || PRODUCTION_API;
    return resolvedApiBase;
  })();
  return resolvePromise;
}

/** True only when /health returns { ok: true }. Use for login UI — ensureApiReady alone is not enough. */
export async function isApiHealthy() {
  const candidates = apiCandidates();
  const bases = [...candidates];
  const fallback = staticApiBase() || PRODUCTION_API;
  if (fallback && !bases.includes(fallback)) bases.push(fallback);
  for (const base of bases) {
    if (await probeHealth(base)) {
      resolvedApiBase = base;
      return true;
    }
  }
  return false;
}

export function resetApiBase() {
  resolvedApiBase = null;
  resolvePromise = null;
}

function friendlyFetchError(err) {
  if (err?.name === 'AbortError') {
    return 'Server is slow — wait a moment and tap Sign in again.';
  }
  if (err?.message === 'Failed to fetch' || err?.name === 'TypeError') {
    return 'Cannot reach API server. Upload lambda\\dist\\HCEN_IP_API.zip to AWS Lambda HCEN_IP_API, wait 30 seconds, then try again. Also check VPN/ad-blocker and internet.';
  }
  return err?.message || 'Request failed';
}

function isRetryable(err) {
  return err?.name === 'AbortError' || err?.message === 'Failed to fetch' || err?.name === 'TypeError';
}

export const API_BASE = staticApiBase();
export const LIVE = process.env.REACT_APP_ENABLE_LIVE_API !== 'false' && !!API_BASE;

async function fetchWithRetry(url, options) {
  let lastErr;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return res;
    } catch (err) {
      clearTimeout(timer);
      lastErr = err;
      if (attempt < MAX_RETRIES && isRetryable(err)) {
        await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

async function parseResponse(res, path, tokenKey) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const raw = data.message || res.statusText || `HTTP ${res.status}`;
    if (res.status === 500) {
      resetApiBase();
      throw new Error('API server error (HTTP 500). Upload C:\\Users\\gurus\\Desktop\\HCEN IP\\lambda\\dist\\HCEN_IP_API.zip to AWS Lambda HCEN_IP_API, wait 30s, then refresh and retry.');
    }
    throw new Error(sanitizeApiError(raw, tokenKey));
  }
  return data;
}

async function request(method, path, body, tokenKey, { sendAuth = true } = {}) {
  if (!LIVE) {
    throw new Error('API not connected. Rebuild with REACT_APP_IP_API_URL and redeploy to S3.');
  }
  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (sendAuth && tokenKey) {
    const token = localStorage.getItem(tokenKey);
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const base = await ensureApiReady();
  let res;
  try {
    res = await fetchWithRetry(`${base}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (isRetryable(err)) {
      resetApiBase();
      const fallback = await ensureApiReady();
      if (fallback !== base) {
        try {
          res = await fetchWithRetry(`${fallback}${path}`, {
            method,
            headers,
            body: body ? JSON.stringify(body) : undefined,
          });
        } catch (retryErr) {
          throw new Error(friendlyFetchError(retryErr));
        }
      } else {
        throw new Error(friendlyFetchError(err));
      }
    } else {
      throw new Error(friendlyFetchError(err));
    }
  }
  return parseResponse(res, path, tokenKey);
}

function staffRequest(method, path, body, opts) {
  return request(method, path, body, 'hcen_ip_token', opts);
}

function platformRequest(method, path, body, opts) {
  return request(method, path, body, 'hcen_platform_token', opts);
}

function qs(params) {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const ipApi = {
  health: () => staffRequest('GET', '/health', undefined, { sendAuth: false }),

  login: (email, password) =>
    staffRequest('POST', '/auth/login', { email, password, portal: 'networks' }, { sendAuth: false }),

  getPatients: (search, limit = 30) => staffRequest('GET', `/patients${qs({ search, limit })}`),
  createPatient: (body) => staffRequest('POST', '/patients', body),
  importPatients: (patients) => staffRequest('POST', '/patients/import', { patients }),
  getPatientDocuments: (patientId) => staffRequest('GET', `/patients/${patientId}/documents`),
  uploadPatientDocument: (patientId, body) => staffRequest('POST', `/patients/${patientId}/documents`, body),
  patchPatient: (patientId, body) => staffRequest('PATCH', `/patients/${patientId}`, body),
  deletePatient: (patientId) => staffRequest('DELETE', `/patients/${patientId}`),
  mergePatients: (keepPatientId, mergePatientId) =>
    staffRequest('POST', '/patients/merge', { keepPatientId, mergePatientId }),

  getQueue: (opts = {}) => staffRequest('GET', `/appointments/queue${qs(opts)}`),
  addToQueue: (body) => staffRequest('POST', '/appointments/queue', body),
  patchQueue: (id, patch) => {
    const body = typeof patch === 'string' ? { status: patch } : patch;
    return staffRequest('PATCH', `/appointments/queue/${id}`, body);
  },
  deleteQueue: (id) => staffRequest('DELETE', `/appointments/queue/${id}`),

  getEmrSettings: () => staffRequest('GET', '/emr/settings'),
  patchEmrSettings: (body) => staffRequest('PATCH', '/emr/settings', body),
  exportPatient: (patientId) => staffRequest('GET', `/patients/${patientId}/export`),

  createOpReceipt: (body) => staffRequest('POST', '/billing/op/receipt', body),
  getReceipts: (limit = 50) => staffRequest('GET', `/billing/receipts${qs({ limit })}`),

  getAdmissions: (limit = 50, opts = {}) => staffRequest('GET', `/ipd/admissions${qs({ limit, ...opts })}`),
  admitPatient: (body) => staffRequest('POST', '/ipd/admissions', body),
  getBeds: () => staffRequest('GET', '/ipd/beds'),
  discharge: (admissionId) => staffRequest('POST', '/ipd/discharge', { admissionId }),
  patchAdmission: (admissionId, body) => staffRequest('PATCH', `/ipd/admissions/${admissionId}`, body),
  getAdmission: (admissionId) => staffRequest('GET', `/ipd/admissions/${admissionId}`),
  addAdmissionActivity: (admissionId, body) => staffRequest('POST', `/ipd/admissions/${admissionId}/activity`, body),
  patchAdmissionActivity: (admissionId, entryId, body) => staffRequest('PATCH', `/ipd/admissions/${admissionId}/activity/${entryId}`, body),
  addAdmissionCharge: (admissionId, body) => staffRequest('POST', `/ipd/admissions/${admissionId}/charges`, body),

  getDischargeSummaries: (limit = 60) => staffRequest('GET', `/ipd/discharge-summaries${qs({ limit })}`),
  getDischargeSummary: (admissionId) => staffRequest('GET', `/ipd/discharge-summaries/${admissionId}`),
  saveDischargeSummary: (body) => staffRequest('POST', '/ipd/discharge-summaries', body),

  getEmrRecords: (patientId, limit = 20) => staffRequest('GET', `/emr${qs({ patientId, limit })}`),
  saveEmr: (body) => staffRequest('POST', '/emr', body),

  getPharmacyOrders: (limit = 40) => staffRequest('GET', `/pharmacy/orders${qs({ limit })}`),
  createPharmacyOrder: (body) => staffRequest('POST', '/pharmacy/orders', body),
  patchPharmacyOrder: (id, body) => staffRequest('PATCH', `/pharmacy/orders/${id}`, body),

  getPharmacyInventory: (search = '') => staffRequest('GET', `/pharmacy/inventory${qs({ search })}`),
  createInventoryItem: (body) => staffRequest('POST', '/pharmacy/inventory', body),
  updateInventoryItem: (itemId, body) => staffRequest('PATCH', `/pharmacy/inventory/${itemId}`, body),

  getPharmacySuppliers: (limit = 100) => staffRequest('GET', `/pharmacy/suppliers${qs({ limit })}`),
  createPharmacySupplier: (body) => staffRequest('POST', '/pharmacy/suppliers', body),
  patchPharmacySupplier: (supplierId, body) => staffRequest('PATCH', `/pharmacy/suppliers/${supplierId}`, body),

  getPharmacyPurchaseOrders: (limit = 80) => staffRequest('GET', `/pharmacy/purchase-orders${qs({ limit })}`),
  createPharmacyPurchaseOrder: (body) => staffRequest('POST', '/pharmacy/purchase-orders', body),
  patchPharmacyPurchaseOrder: (poId, body) => staffRequest('PATCH', `/pharmacy/purchase-orders/${poId}`, body),

  getPharmacyReturns: (limit = 80) => staffRequest('GET', `/pharmacy/returns${qs({ limit })}`),
  createPharmacyReturn: (body) => staffRequest('POST', '/pharmacy/returns', body),
  patchPharmacyReturn: (returnId, body) => staffRequest('PATCH', `/pharmacy/returns/${returnId}`, body),

  getLabOrders: (limit = 40) => staffRequest('GET', `/lab/orders${qs({ limit })}`),
  createLabOrder: (body) => staffRequest('POST', '/lab/orders', body),
  patchLabOrder: (id, body) => staffRequest('PATCH', `/lab/orders/${id}`, body),

  getLabCatalog: (search = '') => staffRequest('GET', `/lab/catalog${qs({ search })}`),
  createLabTest: (body) => staffRequest('POST', '/lab/catalog', body),
  updateLabTest: (testId, body) => staffRequest('PATCH', `/lab/catalog/${testId}`, body),

  getRadiologyOrders: (opts = {}) => staffRequest('GET', `/radiology/orders${qs(opts)}`),
  createRadiologyOrder: (body) => staffRequest('POST', '/radiology/orders', body),
  patchRadiologyOrder: (orderId, body) => staffRequest('PATCH', `/radiology/orders/${orderId}`, body),

  getAnalytics: (opts = {}) => staffRequest('GET', `/analytics/summary${qs(opts)}`),

  getExpenses: (limit = 80) => staffRequest('GET', `/billing/expenses${qs({ limit })}`),
  createExpense: (body) => staffRequest('POST', '/billing/expenses', body),

  getTpaClaims: (opts = {}) => staffRequest('GET', `/tpa/claims${qs(opts)}`),
  createTpaClaim: (body) => staffRequest('POST', '/tpa/claims', body),
  patchTpaClaim: (claimId, body) => staffRequest('PATCH', `/tpa/claims/${claimId}`, body),
  getTpaEmpanelment: () => staffRequest('GET', '/tpa/empanelment'),
  createTpaEmpanelment: (body) => staffRequest('POST', '/tpa/empanelment', body),

  getSmsLog: (limit = 80) => staffRequest('GET', `/sms/log${qs({ limit })}`),
  sendSms: (body) => staffRequest('POST', '/sms/send', body),

  getVisitors: (opts = {}) => staffRequest('GET', `/visitors${qs(opts)}`),
  createVisitor: (body) => staffRequest('POST', '/visitors', body),
  patchVisitor: (visitorId, body) => staffRequest('PATCH', `/visitors/${visitorId}`, body),

  getAmbulanceFleet: () => staffRequest('GET', '/ambulance/fleet'),
  createAmbulance: (body) => staffRequest('POST', '/ambulance/fleet', body),
  patchAmbulance: (ambulanceId, body) => staffRequest('PATCH', `/ambulance/fleet/${ambulanceId}`, body),
  getAmbulanceTrips: (limit = 80) => staffRequest('GET', `/ambulance/trips${qs({ limit })}`),
  createAmbulanceTrip: (body) => staffRequest('POST', '/ambulance/trips', body),
  patchAmbulanceTrip: (tripId, body) => staffRequest('PATCH', `/ambulance/trips/${tripId}`, body),

  getStaff: () => staffRequest('GET', '/staff'),
  getStaffDirectory: () => staffRequest('GET', '/staff/directory'),
  patchHospitalProfile: (body) => staffRequest('PATCH', '/hospital/profile', body),
  getStaffQuotas: () => staffRequest('GET', '/staff/quotas'),
  createStaff: (body) => staffRequest('POST', '/staff', body),
  patchStaff: (staffId, body) => staffRequest('PATCH', `/staff/${staffId}`, body),
  patchStaffProfile: (body) => staffRequest('PATCH', '/staff/me', body),
  resetStaffPassword: (staffId, body) => staffRequest('POST', `/staff/${staffId}/reset-password`, body || {}),

  getManagerOverview: () => staffRequest('GET', '/manager/overview'),

  getHrAttendance: (date) => staffRequest('GET', `/hr/attendance${qs({ date })}`),
  saveHrAttendance: (body) => staffRequest('POST', '/hr/attendance', body),
  getHrLeave: (limit = 80) => staffRequest('GET', `/hr/leave${qs({ limit })}`),
  createHrLeave: (body) => staffRequest('POST', '/hr/leave', body),
  patchHrLeave: (leaveId, body) => staffRequest('PATCH', `/hr/leave/${leaveId}`, body),
  getHrPerformance: (month) => staffRequest('GET', `/hr/performance${qs({ month })}`),
  saveHrPerformance: (body) => staffRequest('PUT', '/hr/performance', body),
  getHrPayroll: (month) => staffRequest('GET', `/hr/payroll${qs({ month })}`),
  saveHrPayroll: (body) => staffRequest('PUT', '/hr/payroll', body),

  getMaintenanceEquipment: (limit = 100) => staffRequest('GET', `/maintenance/equipment${qs({ limit })}`),
  createMaintenanceEquipment: (body) => staffRequest('POST', '/maintenance/equipment', body),
  patchMaintenanceEquipment: (equipmentId, body) => staffRequest('PATCH', `/maintenance/equipment/${equipmentId}`, body),

  getHousekeepingLinen: () => staffRequest('GET', '/housekeeping/linen'),
  saveHousekeepingLinen: (body) => staffRequest('PUT', '/housekeeping/linen', body),

  getHmsRecords: (type, limit = 120) => staffRequest('GET', `/hms/records${qs({ type, limit })}`),
  createHmsRecord: (body) => staffRequest('POST', '/hms/records', body),
  patchHmsRecord: (recordId, body) => staffRequest('PATCH', `/hms/records/${recordId}`, body),
  getHmsConfig: (type) => staffRequest('GET', `/hms/config${qs({ type })}`),
  saveHmsConfig: (body) => staffRequest('PUT', '/hms/config', body),

  abdmStatus: () => staffRequest('GET', '/abdm/status'),
  abdmSearchAbha: (body) => staffRequest('POST', '/abdm/abha/search', body),
  abdmLoginRequestOtp: (body) => staffRequest('POST', '/abdm/abha/login/request-otp', body),
  abdmLoginVerifyOtp: (body) => staffRequest('POST', '/abdm/abha/login/verify', body),
  abdmLoginVerifyUser: (body) => staffRequest('POST', '/abdm/abha/login/verify-user', body),
  abdmEnrollmentRequestOtp: (body) => staffRequest('POST', '/abdm/abha/enrollment/request-otp', body),
  abdmEnrollmentEnrol: (body) => staffRequest('POST', '/abdm/abha/enrollment/enrol', body),
  abdmAbhaSuggestions: (txnId, xToken) => staffRequest('GET', `/abdm/abha/suggestions${qs({ txnId, xToken })}`),
  abdmCreateAbhaAddress: (body) => staffRequest('POST', '/abdm/abha/address', body),
  abdmRegisterBridge: (url) => staffRequest('POST', '/abdm/bridge/url', { url }),

  getDietOrders: (limit = 100) => staffRequest('GET', `/diet/orders${qs({ limit })}`),
  saveDietOrder: (admissionId, body) => staffRequest('PUT', `/diet/orders/${admissionId}`, body),

  getMaintenanceComplaints: (limit = 100) => staffRequest('GET', `/maintenance/complaints${qs({ limit })}`),
  createMaintenanceComplaint: (body) => staffRequest('POST', '/maintenance/complaints', body),
  patchMaintenanceComplaint: (complaintId, body) => staffRequest('PATCH', `/maintenance/complaints/${complaintId}`, body),

  getHousekeepingTasks: (limit = 100) => staffRequest('GET', `/housekeeping/tasks${qs({ limit })}`),
  createHousekeepingTask: (body) => staffRequest('POST', '/housekeeping/tasks', body),
  patchHousekeepingTask: (taskId, body) => staffRequest('PATCH', `/housekeeping/tasks/${taskId}`, body),

  getRoster: (month, dept) => staffRequest('GET', `/roster${qs({ month, dept })}`),
  saveRoster: (body) => staffRequest('PUT', '/roster', body),

  platformLogin: (email, password) =>
    platformRequest('POST', '/platform/auth/login', { email, password }, { sendAuth: false }),

  platformDashboard: () => platformRequest('GET', '/platform/dashboard'),
  platformHospitals: (opts) => platformRequest('GET', `/platform/hospitals${qs(opts)}`),
  platformHospital: (id, opts) => platformRequest('GET', `/platform/hospitals/${id}${qs(opts)}`),
  platformHospitalMonitor: (id, opts) => platformRequest('GET', `/platform/hospitals/${id}/monitor${qs(opts)}`),
  platformPatchHospital: (id, body) => platformRequest('PATCH', `/platform/hospitals/${id}`, body),
  platformDeleteHospital: (id) => platformRequest('DELETE', `/platform/hospitals/${id}`),
  platformResetOwnerPassword: (id) => platformRequest('POST', `/platform/hospitals/${id}/reset-owner-password`, {}),
  platformCreateStaff: (hospitalId, body) => platformRequest('POST', `/platform/hospitals/${hospitalId}/staff`, body),
  platformPatchStaff: (hospitalId, staffId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/staff/${staffId}`, body),
  platformResetStaffPassword: (hospitalId, staffId, body) => platformRequest('POST', `/platform/hospitals/${hospitalId}/staff/${staffId}/reset-password`, body || {}),
  platformPatchBedRates: (hospitalId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/beds`, body),
  platformPatchPatient: (hospitalId, patientId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/patients/${patientId}`, body),
  platformPatchQueue: (hospitalId, queueId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/queue/${queueId}`, body),
  platformAddBed: (hospitalId, body) => platformRequest('POST', `/platform/hospitals/${hospitalId}/beds`, body),
  platformPatchBed: (hospitalId, bedId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/beds/${bedId}`, body),
  platformPatchLabOrder: (hospitalId, orderId, body) => platformRequest('PATCH', `/platform/hospitals/${hospitalId}/lab/${orderId}`, body),
  platformCreateHospital: (body) => platformRequest('POST', '/platform/hospitals', body),
  platformOnboardHospital: (body) => platformRequest('POST', '/platform/hospitals/onboard', body),
  platformAnalytics: (opts = {}) => platformRequest('GET', `/platform/analytics${qs(opts)}`),

  platformAdmins: () => platformRequest('GET', '/platform/admins'),
  platformCreateAdmin: (body) => platformRequest('POST', '/platform/admins', body),
  platformPatchAdmin: (adminId, body) => platformRequest('PATCH', `/platform/admins/${adminId}`, body),
  platformFleetAudit: (opts = {}) => platformRequest('GET', `/platform/audit${qs(opts)}`),
  platformRestoreHospital: (id) => platformRequest('PATCH', `/platform/hospitals/${id}`, { restore: true }),

  /** Cross-account OP link (separate AWS). Generate IP-… code for OP admin, or accept OP-… code here. */
  platformGenerateOpLink: (hospitalId) =>
    platformRequest('POST', `/platform/hospitals/${hospitalId}/op-link/generate`, {}),
  platformAcceptOpLink: (hospitalId, body) =>
    platformRequest('POST', `/platform/hospitals/${hospitalId}/op-link/accept`, body),
  platformUnlinkOp: (hospitalId) =>
    platformRequest('POST', `/platform/hospitals/${hospitalId}/op-link/unlink`, {}),
};

export function isLiveApi() {
  return LIVE;
}

export function setStaffToken(token) {
  if (token) localStorage.setItem('hcen_ip_token', token);
}

export function getApiBase() {
  return resolvedApiBase || API_BASE;
}
